import classNames from 'classnames';
import { useLang } from 'context/langContext';
import Analytics from 'helpers/Analytics';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useRef, useState } from 'react';
import { Namespace } from '../../enums/namespaces';
import { PlaylistType } from '../../enums/playlistType';
import { truncateTitle, Permalink, slugifyString } from '../../helpers';
import { useCategoryRowSwiper, useOnScrollElement } from '../../hooks';
import { Play, PlayBase, PlayState } from '../../typings/api';
import AppLink from '../AppLink/AppLink';
import DramoxImage from 'components/DramoxImage';
import { useMemo } from 'react';

type Plays = PlayBase | Play;
interface Props {
  title: string;
  moreHref?: string;
  playlistType: PlaylistType;
  plays: Plays[];
  theater?: string;
  id?: number;
  showResume?: boolean;
  largeThumbs?: boolean;
  mediumThumbs?: boolean;
  centered?: boolean;
}

const CategorySection = ({
  title,
  plays,
  theater,
  playlistType,
  id,
  moreHref,
  showResume = false,
  mediumThumbs = false,
  largeThumbs = false,
  centered = false,
}: Props): JSX.Element => {
  const ref = useRef(null);
  const { t } = useTranslation(Namespace.Common);
  const { locale } = useLang();
  const [elementTop, setElementTop] = useState<number>(null);

  const query = {};
  if (playlistType !== PlaylistType.Theater) {
    if (id) {
      query['id'] = id;
    }
    query['type'] = playlistType;
  }

  useCategoryRowSwiper(ref, plays);

  const [imageWidth, imageHeight, isImagePortrait] = useMemo(() => {
    let width = 256;
    let height = 144;

    if (mediumThumbs) {
      height = 351;
    } else if (largeThumbs) {
      width = 367;
      height = 504;
    }
    return [width, height, height > width];
  }, [largeThumbs, mediumThumbs]);

  const cls = ['category-section'];
  if (mediumThumbs) {
    cls.push('category-section-medium');
  } else if (largeThumbs) {
    cls.push('category-section-large');
  }

  useEffect(() => {
    if (ref?.current) {
      // calculate top only once on initial render
      setElementTop(ref?.current?.getBoundingClientRect().top);
    }
  }, [ref]);

  const loadImages = useOnScrollElement(elementTop);

  return (
    <div className={classNames('category-section-bg', centered ? 'centered' : null)}>
      <div className={cls.join(' ')}>
        <h2>
          {title}
          {Boolean(moreHref) && (
            <AppLink href={{ pathname: moreHref }}>
              <a>{t('more')}</a>
            </AppLink>
          )}
        </h2>
        <div ref={ref} className="scroll-wrapper">
          <ul className="play-list swiper-wrapper">
            {plays?.map((play) => (
              <li
                key={play.id}
                className={classNames('swiper-slide', showResume ? 'resume' : '', play.is_original ? 'original' : null)}
              >
                <AppLink href={Permalink.play(play, locale)}>
                  <a>
                    <div className="category-image">
                      <DramoxImage
                        loadImmediately={loadImages}
                        placeholderClass="img-placeholder-category-playlist"
                        src={play.poster_url}
                        alt={play.title}
                        width={imageWidth}
                        height={imageHeight}
                        quality={90}
                        usePlaceholder={true}
                      />
                    </div>
                    {play.state === PlayState.PROMOTED ? <p className="coming-soon">{t('coming_soon')}</p> : null}
                    {!isImagePortrait && (
                      <>
                        <h3>{truncateTitle(play.title)}</h3>
                        <p>{theater ? theater : (play as PlayBase).theatre.name}</p>
                      </>
                    )}
                    {isImagePortrait && (
                      <>
                        <h3>
                          {play.logo_url && largeThumbs && <img src={play.logo_url} alt={play.title} />}
                          <span>{truncateTitle(play.title)}</span>
                        </h3>
                        <p>{theater ? theater : (play as PlayBase).theatre.name}</p>
                      </>
                    )}
                    {showResume && (
                      <span>
                        <span
                          style={{
                            width: `${((play as Play).video?.user_progress / (play as Play).video?.runtime) * 100}%`,
                          }}
                        ></span>
                      </span>
                    )}
                  </a>
                </AppLink>
                {showResume && (
                  <a
                    className="play"
                    title={t('common:play_btn')}
                    onClick={() => {
                      window.location.href = Permalink.watch(play, locale);
                    }}
                  ></a>
                )}
              </li>
            ))}
          </ul>
          <a href="#" className="control prev" title={t('common:previous_play')}>
            {t('common:previous_play')}
          </a>
          <a href="#" className="control next" title={t('common:next_play')}>
            {t('common:next_play')}
          </a>
        </div>
      </div>
    </div>
  );
};

export default CategorySection;
